import React, { useEffect, useRef, useContext } from "react";
import { BryntumGrid } from "@bryntum/schedulerpro-react";
import { CLIENTS_GRID_CONFIG } from "../../other/constants/ClientViewGridConfig.js";
import BryntumContext from "../context/bryntumContext.jsx";
import { EDITOR_KEYS } from "../../other/constants/EditorKeys.jsx";
import {
  callOnLoadOnColumns, canOpenEditor, debounce
} from "../../other/utils/otherUtils.jsx";
import useRefsInit from "../hooks/useRefsInit.jsx";
import {GRANTS} from "../../other/constants/PrivilegeTypes.jsx";
import {
  getOriginalRecord,
  swivuCheckGridGrants
} from "../../other/utils/utils.jsx";
import AuthContext from "../context/authContext.jsx";
import { processCellMenuItems } from "../../other/utils/gridCellMenuUtils.jsx";
import SwivuSearchBar from "../components/SwivuSearchBar.jsx";
import AlertNotificationBanner from "../components/alertNotificationBanner/AlertNotificationBanner.jsx";
import {WIDGET_KEYS} from "../../other/constants/WidgetKeys.jsx";
import ClientChecklistTab from "../../bryntum-lib/editors/clientsEditorV2/ClientChecklistTab.jsx";
import useEnableViewTracking from "../hooks/useEnableViewTracking.jsx";
import { VIEW_TYPES } from "../../other/constants/OtherKeys.jsx";
import ClientRemindersTab from "../../bryntum-lib/editors/clientsEditorV2/ClientRemindersTab.jsx";
import ClientContactTab from "../../bryntum-lib/editors/clientsEditorV2/ClientContactTab.jsx";
import { STORE_KEYS } from "../../other/constants/StoreKeys.jsx";
import ClientNotesTab from "../../bryntum-lib/editors/clientsEditorV2/ClientNotesTab.jsx";
import ClientFilesTab from "../../bryntum-lib/editors/clientsEditorV2/ClientFilesTab.jsx";

const EDITOR_TABS = {
  [WIDGET_KEYS.checklistColumn]: ClientChecklistTab.type,
  [WIDGET_KEYS.alertBellColumn]: ClientRemindersTab.type,
  [WIDGET_KEYS.contactColumn]: ClientContactTab.type,
  [WIDGET_KEYS.notesColumn]: ClientNotesTab.type,
  [WIDGET_KEYS.filesColumn]: ClientFilesTab.type
}

const ClientPage = () => {
  const gridRef = useRef(null);
  const { isRefsInit, setRefs } = useRefsInit([gridRef]);
  const { user } = useContext(AuthContext);
  const context = useContext(BryntumContext);
  const { project, editorHandler, stateProvider } = context;
  useEnableViewTracking(gridRef, VIEW_TYPES.clients, isRefsInit);

  useEffect(() => {
    // Assign custom project store to the grid
    if (project) {
      gridRef.current.instance.store = project.clientsStore;
      gridRef.current.instance.store.relayAll(gridRef.current.instance, "swivuClientStore");
      // Below function is being called from setting columns (tag, status, category, team, etc)
      gridRef.current.instance.onClickSwivuEditMessage = ({ tabName }) => {
        editorHandler.openEditor(EDITOR_KEYS.settingGeneralEditor, null, { selectTabByTypeOnOpen: tabName }, { by: "recordType", useExisting: true });
      }
      swivuCheckGridGrants(gridRef.current.instance, user);
      callOnLoadOnColumns(gridRef.current.instance.columns, project, { editorHandler });
    }
  }, [gridRef, project, user, editorHandler]);

  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.instance.features.cellMenu.processItems = processCellMenuItems({
        entityName: "Client",
        editorHandler,
        grid: gridRef.current.instance,
        user,
        project,
        tagStoreKey: STORE_KEYS.clientTagValuesStore,
        promptOnRemove: true,
        onEditEntity(record) {
          editorHandler.openEditor(EDITOR_KEYS.clientEditorV2, getOriginalRecord(record), { client: record });
        }
      });
    }
  }, [editorHandler, user]);

  const onGridCellClick = debounce(({ column, record, event }) => {
    const originalRecord = getOriginalRecord(record);

    // if (event.shiftKey) {
    //   openEntityManager({
    //     selectedBoard: context.selectedBoard,
    //     recordId: originalRecord.id,
    //     recordType: ENTITY_TYPES_KEYS.client,
    //     history: context.history,
    //     viewManager: context.viewManager
    //   });
    //   return;
    // }

    if (canOpenEditor(column, originalRecord) && event.detail === 1 && (column.isTagColumnBase || column.isTeamColumn)) {
      editorHandler.openEditor(EDITOR_KEYS.entityTagEditor, null, {
        entityRecord: originalRecord,
        event,
        column
      }, { by: "recordType", useExisting: true });
    }
  }, 300);

  return (
    <>
      <SwivuSearchBar searchableRefs={[gridRef]} />
      {user && (
        <AlertNotificationBanner project={project} editorHandler={editorHandler} stateProvider={stateProvider} user={user} />
      )}
      <div className="main-grid-view main-view-clients" style={{ height: "100%", width: "100%", display: "flex" }}>
        <BryntumGrid
          ref={setRefs(gridRef)}
          {...CLIENTS_GRID_CONFIG}
          disabled={user && !user.can(GRANTS.grant_four)}
          readOnly={user && !user.can(GRANTS.grant_four)}
          onCellClick={onGridCellClick}
          onCellDblClick={({ record, column }) => {
            const originalRecord = getOriginalRecord(record);
            if (canOpenEditor(column, originalRecord)) {
              editorHandler.openEditor(EDITOR_KEYS.clientEditorV2, originalRecord, {
                client: originalRecord,
                selectTabByTypeOnOpen: EDITOR_TABS[column.type] || null
              });
            }
          }}
          onHeaderMenuItem={({ source, item }) => {
            if (item._menuId === "client") {
              const newRecord = source.store.createRecord();
              editorHandler.openEditor(EDITOR_KEYS.clientEditorV2, newRecord, { client: newRecord });
            }
          }}
        />
      </div>
    </>
  );
};

export default ClientPage;
